import React, { useState, useEffect } from 'react';
import { adminAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area
} from 'recharts';
import {
  Activity,
  Database,
  Zap,
  Clock,
  AlertTriangle,
  CheckCircle,
  Server,
  RefreshCw,
  Loader2,
  TrendingUp,
  Users,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';

const HealthDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [history, setHistory] = useState([]);
  const [period, setPeriod] = useState('24h');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [period]);

  const loadData = async () => {
    try {
      const [metricsRes, historyRes] = await Promise.all([
        adminAPI.getHealthMetrics(),
        adminAPI.getHealthHistory(period)
      ]);
      setMetrics(metricsRes.data);
      setHistory(historyRes.data.metrics || []);
    } catch (error) {
      console.error('Error loading health data:', error);
      // Don't show error toast on background refresh
      if (loading) {
        toast.error('Erreur lors du chargement des métriques');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadData();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'operational': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'degraded': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'critical': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'operational': return <CheckCircle className="w-5 h-5 text-emerald-500" />;
      case 'degraded': return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      case 'critical': return <AlertTriangle className="w-5 h-5 text-red-500" />;
      default: return <Activity className="w-5 h-5 text-slate-500" />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'operational': return 'Opérationnel';
      case 'degraded': return 'Dégradé';
      case 'critical': return 'Critique';
      default: return 'Inconnu';
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Card className="border-0 shadow-sm">
          <CardContent className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-slate-300 animate-spin" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="health-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-[#1A237E]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Santé du Système
          </h2>
          <p className="text-slate-500 text-sm mt-1">
            Surveillance en temps réel de la plateforme
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-36 h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1h">Dernière heure</SelectItem>
              <SelectItem value="24h">24 heures</SelectItem>
              <SelectItem value="7d">7 jours</SelectItem>
            </SelectContent>
          </Select>
          
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleRefresh}
            disabled={refreshing}
            className="h-9"
          >
            {refreshing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
          </Button>
        </div>
      </div>

      {/* Status Banner */}
      {metrics && (
        <Card className={`border-2 ${getStatusColor(metrics.status)}`}>
          <CardContent className="py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {getStatusIcon(metrics.status)}
              <div>
                <p className="font-semibold">{getStatusLabel(metrics.status)}</p>
                <p className="text-sm opacity-80">
                  Dernière mise à jour: {new Date(metrics.timestamp).toLocaleTimeString('fr-FR')}
                </p>
              </div>
            </div>
            <Badge className={getStatusColor(metrics.status)}>
              Uptime: {metrics.system?.uptime_days || 0} jours
            </Badge>
          </CardContent>
        </Card>
      )}

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Database Latency */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                <Database className="w-4 h-4 text-blue-600" />
              </div>
              <span className="text-sm text-slate-500">Base de données</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">
              {metrics?.database?.latency_ms || 0} ms
            </p>
            <Badge className={
              metrics?.database?.status === 'healthy' 
                ? 'bg-emerald-100 text-emerald-700' 
                : 'bg-red-100 text-red-700'
            }>
              {metrics?.database?.status === 'healthy' ? 'Sain' : 'Erreur'}
            </Badge>
          </CardContent>
        </Card>

        {/* API Calls */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                <Zap className="w-4 h-4 text-purple-600" />
              </div>
              <span className="text-sm text-slate-500">Appels API (1h)</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">
              {metrics?.api?.calls_last_hour || 0}
            </p>
            <p className="text-xs text-slate-500">
              Temps moyen: {metrics?.api?.avg_response_time_ms || 0} ms
            </p>
          </CardContent>
        </Card>

        {/* Error Rate */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                (metrics?.api?.error_rate_percent || 0) < 5 
                  ? 'bg-emerald-100' 
                  : 'bg-red-100'
              }`}>
                <AlertTriangle className={`w-4 h-4 ${
                  (metrics?.api?.error_rate_percent || 0) < 5 
                    ? 'text-emerald-600' 
                    : 'text-red-600'
                }`} />
              </div>
              <span className="text-sm text-slate-500">Taux d'erreur</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">
              {metrics?.api?.error_rate_percent || 0}%
            </p>
            <p className="text-xs text-slate-500">
              {metrics?.api?.errors_last_hour || 0} erreurs cette heure
            </p>
          </CardContent>
        </Card>

        {/* Active Users */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                <Users className="w-4 h-4 text-amber-600" />
              </div>
              <span className="text-sm text-slate-500">Activité aujourd'hui</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">
              {metrics?.activity?.active_users_today || 0}
            </p>
            <p className="text-xs text-slate-500">
              {metrics?.activity?.appointments_today || 0} RDV
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* API Calls Chart */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="w-4 h-4 text-purple-500" />
              Appels API
            </CardTitle>
            <CardDescription>Volume de requêtes sur la période</CardDescription>
          </CardHeader>
          <CardContent>
            {history.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={history}>
                  <defs>
                    <linearGradient id="colorCalls" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="time" tick={{ fontSize: 10 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" />
                  <Tooltip 
                    formatter={(value) => [value, 'Appels']}
                    labelFormatter={(label) => `Heure: ${label}`}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="api_calls" 
                    stroke="#8B5CF6" 
                    fillOpacity={1}
                    fill="url(#colorCalls)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-slate-400">
                <p className="text-sm">Aucune donnée disponible</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Response Time Chart */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="w-4 h-4 text-blue-500" />
              Temps de réponse
            </CardTitle>
            <CardDescription>Latence moyenne (ms)</CardDescription>
          </CardHeader>
          <CardContent>
            {history.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={history}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="time" tick={{ fontSize: 10 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" />
                  <Tooltip 
                    formatter={(value) => [`${value} ms`, 'Latence']}
                    labelFormatter={(label) => `Heure: ${label}`}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="avg_response_time" 
                    stroke="#3B82F6" 
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-slate-400">
                <p className="text-sm">Aucune donnée disponible</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Error Rate Chart */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-500" />
            Taux d'erreur
          </CardTitle>
          <CardDescription>Pourcentage d'erreurs sur la période</CardDescription>
        </CardHeader>
        <CardContent>
          {history.length > 0 ? (
            <ResponsiveContainer width="100%" height={150}>
              <AreaChart data={history}>
                <defs>
                  <linearGradient id="colorErrors" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#EF4444" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#EF4444" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="time" tick={{ fontSize: 10 }} stroke="#94a3b8" />
                <YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" domain={[0, 'auto']} />
                <Tooltip 
                  formatter={(value) => [`${value}%`, 'Taux d\'erreur']}
                  labelFormatter={(label) => `Heure: ${label}`}
                />
                <Area 
                  type="monotone" 
                  dataKey="error_rate" 
                  stroke="#EF4444" 
                  fillOpacity={1}
                  fill="url(#colorErrors)"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[150px] flex items-center justify-center text-slate-400">
              <p className="text-sm">Aucune donnée disponible</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default HealthDashboard;
